import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Lead, TimelineItem } from "@/types";
import { useLeads } from "@/hooks/useLeads";
import { useTasks } from "@/hooks/useTasks";
import { useAuth } from "@/contexts/AuthContext";
import { getAllActiveUsers } from "@/services/userService";
import { TEST_ADMIN_USER_ID } from "@/lib/constants";
import { sendLeadSharedNotification } from "@/services/notificationService";
import { toast } from "sonner";
import { UserPlus, Loader2 } from "lucide-react";
import { useModal } from "@/contexts/ModalContext";

interface ShareLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead;
}

export function ShareLeadDialog({ open, onOpenChange, lead }: ShareLeadDialogProps) {
  const { updateLead } = useLeads();
  const { tasks, updateTask } = useTasks();
  const { user } = useAuth();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const { registerModal, unregisterModal } = useModal();

  useEffect(() => {
    if (!open) return;
    registerModal('lead-share-dialog', () => onOpenChange(false), 1);
    return () => unregisterModal('lead-share-dialog');
  }, [open, onOpenChange, registerModal, unregisterModal]);

  // Load users from Firebase
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await getAllActiveUsers();
        setAvailableUsers(usersData.filter(u => (u.userId || u.id) !== TEST_ADMIN_USER_ID));
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, []);

  // Initialize selected users from lead.sharedWith whenever lead changes or dialog opens
  useEffect(() => {
    if (open) {
      setSelectedUsers(lead.sharedWith || []);
      setNote("");
    }
  }, [open, lead.sharedWith]);

  // Check if user is the owner or assignee (cannot be unshared)
  const isPermanentAccess = (userId: string) => {
    return lead.createdBy === userId || lead.assignedTo === userId;
  };

  const handleToggleUser = (userId: string) => {
    if (isPermanentAccess(userId)) return;
    
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleUpdateShare = async () => {
    // Calculate added and removed users
    const initialSharedWith = lead.sharedWith || [];
    const addedUsers = selectedUsers.filter(id => !initialSharedWith.includes(id));
    const removedUsers = initialSharedWith.filter(id => !selectedUsers.includes(id));

    if (addedUsers.length === 0 && removedUsers.length === 0) {
      onOpenChange(false);
      return;
    }

    setSaving(true);
    try {

    // Get names for timeline/notifications
    const addedUserNames = availableUsers
      .filter(u => addedUsers.includes(u.userId || u.id))
      .map(u => u.name)
      .join(", ");

    const removedUserNames = availableUsers
      .filter(u => removedUsers.includes(u.userId || u.id))
      .map(u => u.name)
      .join(", ");

    // Create timeline notes
    const timelineUpdates: TimelineItem[] = [];

    if (addedUsers.length > 0) {
      timelineUpdates.push({
        id: `timeline_${Date.now()}_add`,
        type: 'note',
        content: note 
          ? `Lead shared with ${addedUserNames}. Note: ${note}`
          : `Lead shared with ${addedUserNames}`,
        createdAt: new Date(),
        createdBy: user?.name || 'Unknown',
      });
    }

    if (removedUsers.length > 0) {
      timelineUpdates.push({
        id: `timeline_${Date.now()}_remove`,
        type: 'note',
        content: `Access removed for ${removedUserNames}`,
        createdAt: new Date(),
        createdBy: user?.name || 'Unknown',
      });
    }

    // Update the lead
    await updateLead(lead.id, {
      sharedWith: selectedUsers,
      timeline: [...lead.timeline, ...timelineUpdates],
    });

    // Auto-share all associated reminders with the same users
    const associatedReminders = tasks.filter(task => task.leadId === lead.id);
    
    if (associatedReminders.length > 0) {
      const reminderUpdates = associatedReminders.map(async (reminder) => {
        // Get current sharedWith list for this reminder
        const currentReminderShared = reminder.sharedWith || [];
        
        // Add newly added users to reminder's sharedWith
        const newReminderShared = Array.from(new Set([
          ...currentReminderShared,
          ...addedUsers
        ]));
        
        // Remove users that were removed from lead
        const finalReminderShared = newReminderShared.filter(
          userId => !removedUsers.includes(userId)
        );
        
        // Only update if there's a change
        if (JSON.stringify(currentReminderShared.sort()) !== JSON.stringify(finalReminderShared.sort())) {
          await updateTask(reminder.id, {
            sharedWith: finalReminderShared.length > 0 ? finalReminderShared : undefined
          });
        }
      });
      
      await Promise.all(reminderUpdates);
    }

    // Send notifications to NEWLY added users only
    if (addedUsers.length > 0) {
      try {
        await sendLeadSharedNotification(
          addedUsers,
          lead,
          user?.name || 'A colleague',
          note
        );
      } catch (error) {
        console.error('Error sending notifications:', error);
      }
    }

    if (addedUsers.length > 0) {
      toast.success(`Lead shared with ${addedUserNames}`);
    }
    if (removedUsers.length > 0) {
      toast.info(`Access removed for ${removedUserNames}`);
    }

    onOpenChange(false);
    } catch (error) {
      console.error('Error sharing lead:', error);
      toast.error('Failed to update lead sharing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Manage Lead Access
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto py-2 space-y-4">
          <div className="space-y-2">
            <Label>Select Colleagues</Label>
            <div className="grid grid-cols-1 gap-2">
              {availableUsers.map((member) => {
                const memberId = member.userId || member.id;
                const isOwnerOrAssignee = isPermanentAccess(memberId);
                const isSelected = selectedUsers.includes(memberId) || isOwnerOrAssignee;
                
                return (
                  <div 
                    key={memberId}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                      isOwnerOrAssignee 
                        ? "bg-secondary/30 opacity-70 cursor-not-allowed" 
                        : "cursor-pointer hover:bg-secondary/50"
                    }`}
                    onClick={() => !isOwnerOrAssignee && handleToggleUser(memberId)}
                  >
                    <Checkbox 
                      checked={isSelected} 
                      disabled={isOwnerOrAssignee}
                      onCheckedChange={() => !isOwnerOrAssignee && handleToggleUser(memberId)}
                    />
                    <div className="flex flex-col flex-1">
                      <span className="text-sm font-medium">{member.name}</span>
                      {isOwnerOrAssignee && (
                        <span className="text-[10px] text-muted-foreground uppercase">
                          {lead.createdBy === memberId ? 'Owner' : 'Assignee'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="shareNote">Add Note (Optional)</Label>
            <Textarea
              id="shareNote"
              placeholder="Add note for newly added users..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 pt-2 border-t mt-auto">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleUpdateShare} disabled={saving}>
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving...</>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
